// React
import React, { useContext } from 'react'
import { Link } from 'react-router-dom'

// UI
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import {
  IconButton,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Select
} from '@material-ui/core'

// Helper
import { translate } from '../../../utils/helpers'

//Component
import jMoment from 'moment-jalaali'

// Type
import { Parcel } from '../interfaces'
import AuthContext from '../../../authContext'
import Can from '../../../components/Can'
import RonadIcons from '../../../components/iconComponent'
import SimpleTable, {
  HeadCell,
  TbodyProps,
  TheadProps
} from '../../../components/Table/simpleTable'
import SimpleTableBody from '../../../components/Table/tableBody'
import SimpleTableHead from '../../../components/Table/tableHead'

type Props = {
  data: any
  handleOpenConfirmDisable: (id: number) => void
  handleOpen: (id: number, type: string) => void
  changeStatus: any
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute'
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px'
    },
    renderValue: {
      paddingLeft: '32px'
    },
    radioGroup: {
      margin: '-8px 0'
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px'
      }
    }
  })
)

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext)
  const classes = useStyles()
  const { handleOpenConfirmDisable, handleOpen } = props

  const action = (row: any) => (
    <>
      {row.type === 'RECEIPT' && (
        <Link to={`/parcels-exit/details/${row.id}`}>
          <IconButton
            aria-label='details'
            size='small'
            title='جزئیات قبض خروج از انبار'
            data-cy='details'
          >
            <RonadIcons name='page' size={1} className='text-green' />
          </IconButton>
        </Link>
      )}
      {row.type !== 'RECEIPT' && row.type !== 'WAREHOUSE' && (
        <Link to={`/parcels/details/${row.id}`}>
          <IconButton
            aria-label='detail'
            size='small'
            title='جزئیات محموله'
            data-cy='detail'
          >
            <RonadIcons name='documentOutline' className='text-green' />
          </IconButton>
        </Link>
      )}
      {row.status === 'CONFLICTED' && (
        <Can
          perform='/parcels-sender/:id/conflicts'
          role={authContext.user.role}
          yes={() => (
            <Link to={`/parcels-sender/${row.id}/conflicts`}>
              <IconButton
                aria-label='conflict'
                title='نمایش مغایرت'
                size='small'
                data-cy='conflict'
              >
                <RonadIcons name='conflict' size={1} className='text-orange' />
              </IconButton>
            </Link>
          )}
        />
      )}
      {row.status == 'SUBMITTED' && (
        <Can
          perform='parcels-sender:editParcel'
          role={authContext.user.role}
          yes={() => (
            <Link to={`/parcels-sender/${row.id}/edit`} id='editParcelIcon'>
              <IconButton
                title='ویرایش'
                size='small'
                onClick={() => handleOpen(row.id, 'edit')}
                data-cy='edit'
              >
                <RonadIcons name='pencil' className='text-primary' />
              </IconButton>
            </Link>
          )}
        />
      )}
      <Can
        perform='/parcels-sender-operators/:id/warehouse/:warehouseId'
        role={authContext.user.role}
        yes={() => (
          <Link
            to={`/parcels-sender-operators/${row.id}/warehouse/${row.sourceId}`}
          >
            <IconButton
              aria-label='assign'
              size='small'
              title='تخصیص محموله'
              data-cy='assign'
            >
              <RonadIcons name='accountPlus' className='text-green' />
            </IconButton>
          </Link>
        )}
      />
      {row.status == 'SUBMITTED' && (
        <Can
          perform='parcels-sender:cancelParcel'
          role={authContext.user.role}
          yes={() => (
            <IconButton
              aria-label='cancel'
              title='لغو محموله '
              size='small'
              onClick={() => handleOpenConfirmDisable(row.id)}
              data-cy='cancel'
            >
              <RonadIcons
                name='times'
                className='pointer text-danger'
                size={0.8}
              />
            </IconButton>
          )}
        />
      )}
    </>
  )

  const headCells: HeadCell<Parcel>[] = [
    {
      id: 'destinationWarehouseName',
      label: 'انبار مقصد'
    },
    {
      id: 'id',
      label: 'شناسه محموله'
    },
    {
      id: 'courierName',
      label: 'جمع آوری',
      format: (row) => (
        <>
          {row.courierName}
          {row.fulfillmentTypeEnabled && ` (${row.fulfillmentTypeName})`}
        </>
      )
    },
    {
      id: 'shippingDate',
      label: 'تاریخ ثبت',
      format: (row) => (
        <>
          <span>
            {jMoment(row.shippingDate, 'YYYY/M/D')
              .locale('fa')
              .format('jYY/jMM/jDD')}
          </span>
        </>
      )
    },
    {
      id: 'deliveryDate',
      label: 'تاریخ تحویل',
      format: (row) => (
        <>
          <span>
            {jMoment(row.deliveryDate, 'YYYY/M/D')
              .locale('fa')
              .format('jYY/jMM/jDD')}
          </span>
        </>
      )
    },
    {
      id: 'deliveryTime',
      label: 'بازه تحویل',
      format: (row) => (
        <>
          <span>
            {jMoment(row.deliveryStartTime, 'hh:mm')
              .locale('fa')
              .format('HH:mm')}
            {` - `}
            {jMoment(row.deliveryEndTime, 'hh:mm').locale('fa').format('HH:mm')}
          </span>
        </>
      )
    },
    {
      id: 'status',
      label: 'وضعیت',
      format: (row) => (
        <>
          <Can
            perform='parcels-sender:changeStatus'
            role={authContext.user.role}
            yes={() => (
              <FormControl style={{ width: '100%' }}>
                <RonadIcons name='pencil' className={classes.changStatusIcon} />
                <Select
                  className={classes.changStatusBox}
                  fullWidth
                  name={`status${index}`}
                  multiple
                  id={row.rubikaId}
                  value={[{}]}
                  IconComponent={() => null}
                  renderValue={() => (
                    <span className={classes.renderValue}>
                      {translate(row.status, 'parcel')}
                    </span>
                  )}
                >
                  <RadioGroup
                    className={classes.radioGroup}
                    aria-label={`status${index}`}
                    name={`status${index}`}
                    value={'item'}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                      props.changeStatus({
                        id: row.id,
                        body: `${e.target.value}`
                      })
                    }
                  >
                    {['SUBMITTED', 'SHIPPED', 'DELIVERED', 'CANCELLED'].map(
                      (item: any, index: number) => (
                        <FormControlLabel
                          className={classes.radioGroupItem}
                          key={index}
                          checked={row.status === item}
                          value={item}
                          control={<Radio />}
                          label={translate(item, 'parcel')}
                        />
                      )
                    )}
                  </RadioGroup>
                </Select>
              </FormControl>
            )}
            no={() => (
              <span data-cy='status'>{translate(row.status, 'parcel')}</span>
            )}
          />
        </>
      )
    }
  ]

  return (
    <SimpleTable
      Thead={(prop: TheadProps) => (
        <SimpleTableHead headCells={headCells} action={action} {...prop} />
      )}
      Tbody={(prop: TbodyProps) => (
        <SimpleTableBody
          celles={headCells}
          data={props.data}
          action={action}
          {...prop}
        />
      )}
    />
  )
}
